/**
 * Product-aware refresh (Phase 2.2) — picks the refresh endpoint at RUNTIME by product:
 * vitrine → /v1/showcase/auth/refresh, full → /v1/auth/token/refresh. The refresh token rides in the
 * path-scoped HttpOnly cookie (axios withCredentials), so the endpoint must match the product; the
 * persisted `tacita.product` hint drives the choice on cold boot before any access token exists.
 */
import { authApi } from './auth';
import { vitrineAuthApi } from './vitrineAuth';
import { currentProduct } from '@/product';

export interface NormalizedTokens {
  accessToken: string;
  customerId: string;
  sessionId: string;
}

/**
 * Every tab of the app shares one refresh cookie, and each refresh rotates it. Two tabs waking together (switching
 * from Telegram to Safari wakes the old tab next to the new one) sent the same cookie twice and the server took the
 * second one for a stolen token, signing the client out (21.10.2026). One refresh at a time across tabs: the tab that
 * waits sends the cookie the first one just received. Without Web Locks the server's short grace still covers it.
 */
const REFRESH_LOCK = 'tacita-auth-refresh';

// No token arg — the refresh token rides in the HttpOnly cookie (axios withCredentials).
export async function refreshTokens(): Promise<NormalizedTokens> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return refreshOnce();
  return await locks.request(REFRESH_LOCK, refreshOnce);
}

async function refreshOnce(): Promise<NormalizedTokens> {
  if (currentProduct() === 'vitrine') {
    const t = await vitrineAuthApi.refresh();
    if (t.error) throw new Error(t.error);
    return { accessToken: t.accessToken, customerId: t.customerId, sessionId: t.sessionId };
  }
  const t = await authApi.refresh();
  return { accessToken: t.accessToken, customerId: t.customerId, sessionId: t.sessionId };
}
